import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Avatar,
  Divider,
  Alert,
  CircularProgress,
  IconButton,
  Card,
  CardContent,
  Chip
} from '@mui/material';
import {
  PhotoCamera as PhotoCameraIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Work as WorkIcon,
  Business as BusinessIcon,
  SupervisorAccount as SupervisorAccountIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import ProfileChangeRequestForm from '../components/ProfileChangeRequestForm';

const MyProfile = () => {
  const { user: authUser } = useAuth();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showChangeRequestForm, setShowChangeRequestForm] = useState(false);

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      const response = await api.get('/auth/me');
      setUser(response.data.data.user);
    } catch (err) {
      setError('Failed to load profile');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setError('Image size must be less than 2MB');
      return;
    }

    setUploading(true);
    setError('');
    setSuccess('');

    try {
      const formData = new FormData();
      formData.append('photo', file);

      const response = await api.post('/users/photo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setUser(prev => ({ ...prev, photoUrl: response.data.data.photoUrl }));
      setSuccess('Profile photo updated successfully!');

      // Reload page after 1.5 seconds to update header avatar
      setTimeout(() => {
        window.location.reload();
      }, 1500);

    } catch (err) {
      setError(err.response?.data?.message || 'Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value
    });
    setPasswordError('');
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    // Validation
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      setPasswordError('All fields are required');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    setSavingPassword(true);

    try {
      await api.put('/auth/change-password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });

      setPasswordSuccess('Password changed successfully!');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setChangingPassword(false);

      setTimeout(() => setPasswordSuccess(''), 3000);
    } catch (err) {
      setPasswordError(err.response?.data?.message || 'Failed to change password');
    } finally {
      setSavingPassword(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return (
      <Box p={3}>
        <Alert severity="error">Failed to load profile</Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          My Profile
        </Typography>
        
        {user.role !== 'admin' && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<EditIcon />}
            onClick={() => setShowChangeRequestForm(true)}
          >
            Request Profile Change
          </Button>
        )}
      </Box>

      {/* Global Error/Success Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Profile Photo Section */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Box position="relative" display="inline-block">
              <Avatar
                src={user.photoUrl}
                sx={{
                  width: 150,
                  height: 150,
                  mb: 2,
                  fontSize: '4rem',
                  bgcolor: 'primary.main'
                }}
              >
                {!user.photoUrl && user.name.charAt(0).toUpperCase()}
              </Avatar>

              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="photo-upload"
                type="file"
                onChange={handlePhotoUpload}
                disabled={uploading}
              />
              <label htmlFor="photo-upload">
                <IconButton
                  component="span"
                  disabled={uploading}
                  sx={{
                    position: 'absolute',
                    bottom: 16,
                    right: 0,
                    bgcolor: 'primary.main',
                    color: 'white',
                    '&:hover': { bgcolor: 'primary.dark' }
                  }}
                >
                  {uploading ? <CircularProgress size={24} /> : <PhotoCameraIcon />}
                </IconButton>
              </label>
            </Box>

            <Typography variant="h6" gutterBottom>
              {user.name}
            </Typography>
            <Chip
              label={user.role.toUpperCase()}
              color="primary"
              size="small"
              sx={{ mb: 1 }}
            />
            <Typography variant="body2" color="text.secondary">
              {user.createdAt ? `Member since ${new Date(user.createdAt).toLocaleDateString()}` : ''}
            </Typography>
          </Paper>
        </Grid>

        {/* Profile Details Section */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Profile Information
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={3}>
              {/* Name */}
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center" mb={1}>
                  <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    Full Name
                  </Typography>
                </Box>
                <Typography variant="body1" fontWeight="500">
                  {user.name}
                </Typography>
              </Grid>

              {/* Email */}
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center" mb={1}>
                  <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    Email Address
                  </Typography>
                </Box>
                <Typography variant="body1" fontWeight="500">
                  {user.email}
                </Typography>
              </Grid>

              {/* Phone */}
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center" mb={1}>
                  <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    Phone Number
                  </Typography>
                </Box>
                <Typography variant="body1" fontWeight="500">
                  {user.phone || 'Not provided'}
                </Typography>
              </Grid>

              {/* Role */}
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center" mb={1}>
                  <WorkIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    Role
                  </Typography>
                </Box>
                <Typography variant="body1" fontWeight="500">
                  {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                </Typography>
              </Grid>

              {/* Site Allotted (for supervisors) */}
              {user.role === 'supervisor' && user.sites && user.sites.length > 0 && (
                <Grid item xs={12}>
                  <Box display="flex" alignItems="center" mb={1}>
                    <BusinessIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      Site(s) Allotted
                    </Typography>
                  </Box>
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {user.sites.map(site => (
                      <Chip
                        key={site.id}
                        label={site.name}
                        variant="outlined"
                        size="small"
                      />
                    ))}
                  </Box>
                </Grid>
              )}

              {/* Manager (for supervisors) */}
              {user.role === 'supervisor' && user.manager && (
                <Grid item xs={12}>
                  <Box display="flex" alignItems="center" mb={1}>
                    <SupervisorAccountIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      Manager
                    </Typography>
                  </Box>
                  <Typography variant="body1" fontWeight="500">
                    {user.manager.name}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Paper>

          {/* Change Password Section */}
          <Paper sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" fontWeight="bold">
                Change Password
              </Typography>
              {!changingPassword && (
                <Button
                  startIcon={<EditIcon />}
                  onClick={() => setChangingPassword(true)}
                  variant="outlined"
                  size="small"
                >
                  Change
                </Button>
              )}
            </Box>
            <Divider sx={{ mb: 3 }} />

            {changingPassword ? (
              <form onSubmit={handlePasswordSubmit}>
                {passwordError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {passwordError}
                  </Alert>
                )}
                {passwordSuccess && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    {passwordSuccess}
                  </Alert>
                )}

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="Current Password"
                      type="password"
                      name="currentPassword"
                      value={passwordData.currentPassword}
                      onChange={handlePasswordChange}
                      fullWidth
                      required
                      disabled={savingPassword}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="New Password"
                      type="password"
                      name="newPassword"
                      value={passwordData.newPassword}
                      onChange={handlePasswordChange}
                      fullWidth
                      required
                      disabled={savingPassword}
                      helperText="Minimum 6 characters"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Confirm New Password"
                      type="password"
                      name="confirmPassword"
                      value={passwordData.confirmPassword}
                      onChange={handlePasswordChange}
                      fullWidth
                      required
                      disabled={savingPassword}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Box display="flex" gap={2}>
                      <Button
                        type="submit"
                        variant="contained"
                        startIcon={savingPassword ? <CircularProgress size={20} /> : <SaveIcon />}
                        disabled={savingPassword}
                      >
                        {savingPassword ? 'Saving...' : 'Save Password'}
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<CancelIcon />}
                        onClick={() => {
                          setChangingPassword(false);
                          setPasswordData({
                            currentPassword: '',
                            newPassword: '',
                            confirmPassword: ''
                          });
                          setPasswordError('');
                        }}
                        disabled={savingPassword}
                      >
                        Cancel
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </form>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Click "Change" to update your password
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Change Request Modal */}
      {showChangeRequestForm && (
        <ProfileChangeRequestForm
          user={user}
          onClose={() => setShowChangeRequestForm(false)}
          onSuccess={() => {
            setShowChangeRequestForm(false);
            fetchUserProfile();
          }}
        />
      )}
    </Box>
  );
};

export default MyProfile;